"use client";

import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Dense Linear-style table substrate.
 *
 * - `border-separate border-spacing-0` is retained: `border-collapse` breaks a
 *   `position: sticky` header in every engine, and the spacing is zero so the
 *   layout is identical.
 * - Rows carry no rules at all. Separation is the row height plus `hover:bg-hover`
 *   (`docs/borderless-contract.md` §1); the opaque `bg-panel` on the sticky header
 *   is what keeps scrolled rows from showing through it.
 * - Rows are a fixed 40px (`h-row-lg`) — never padding-derived.
 * - Hover is a background wash only; selection adds a 2px accent marker on the
 *   first cell (see `TableCell`).
 */
const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <table
    ref={ref}
    className={cn(
      "w-full caption-bottom border-separate border-spacing-0 text-sm",
      className,
    )}
    {...props}
  />
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      // `bg-panel` deliberately matches the raised container the table sits
      // in, so the header looks seamless with it. That is not an oversight:
      // a sticky header must be OPAQUE or rows scroll visibly underneath it,
      // and the only opaque raised fill is `bg-panel`. A translucent tint
      // (`bg-sunken`) would separate it from the body and break occlusion.
      // Header-vs-body hierarchy comes from type instead — `text-xs`
      // uppercase tertiary against `text-sm` primary.
      "sticky top-0 z-10 bg-panel [&_tr]:h-control-lg [&_tr]:hover:bg-transparent",
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn(className)} {...props} />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    // A totals strip, not a raised bar: `bg-sunken` recedes from the body rows
    // without stepping onto the same tier as the `bg-panel` header, which would
    // leave header and footer indistinguishable from the table container.
    className={cn("bg-sunken font-medium", className)}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "group h-row-lg transition-[background-color] duration-100 ease-out-fast",
      "hover:bg-hover data-[state=selected]:bg-selected data-[focused=true]:bg-hover",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-control-lg whitespace-nowrap px-4 text-left align-middle",
      "text-xs font-medium text-text-tertiary",
      "[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-px",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "h-row-lg px-4 align-middle text-sm text-text",
      // Selected-row marker — first cell only.
      "first:relative first:before:absolute first:before:left-0 first:before:top-0",
      "first:before:h-full first:before:w-0.5 first:before:bg-accent first:before:opacity-0",
      "group-data-[state=selected]:first:before:opacity-100",
      "[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-px",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-2 text-xs text-text-tertiary", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
};
